import { customElement } from "lit/decorators.js";
import * as TagNames from "./segmented-control.tagnames";
import { css, html, LitElement, PropertyValues } from "lit";
import { AtlSegmentedControlProps } from "./segmented-control.props";
import { ButtonEnums } from "../button";
import { getGlobalStyleSheets } from "../../global-styles";
import { AtlSegmentedControlButtonClickEvent } from "./segmented-control.events";
import { classMap } from "lit/directives/class-map.js";
import { enumAttributeConverter } from "../enum-types";
import { AtlSegmentedControlButton } from "./segmented-control-button.component";

@customElement(TagNames.SegmentedControl)
export class AtlSegmentedControl extends LitElement implements AtlSegmentedControlProps {
    declare size?: ButtonEnums.ButtonSize;
    declare showDefaultIcons?: boolean;
    declare evenlySpread?: boolean;
    declare hideLabels?: boolean;
    declare name?: string;
    declare _defaultButtonActiveSet?: boolean;

    static properties = {
        size: { type: String, converter: enumAttributeConverter(ButtonEnums.ButtonSize)},
        name: {type: String },
        value:{ type: String},
        hideLabels: { type: Boolean},
        showDefaultIcons: { type: Boolean},
        evenlySpread: { type: Boolean},
        _defaultButtonActiveSet: {type: Boolean}
    };
    
    static styles = [
        ...getGlobalStyleSheets(),
        css`
            :host {
                width: 100%;
            }

            .segmented-control {
                display: inline-flex;
            }

            .segmented-control.evenly-spread {
                width: 100%;
            }
                
            .evenly-spread ::slotted(*){ 
                flex-grow: 1 !important;
            }
        `,
    ];

    constructor() {
      super();
      this.addEventListener(AtlSegmentedControlButtonClickEvent.EVENT_NAME, (e: Event) => {
        this._segmentedControlButtons.forEach((button) => {
            button.active = false;
        });
        
        (e.target as AtlSegmentedControlButton).active = true;
      });
      this._defaultButtonActiveSet = false;
    }

    protected override updated(changedProperties: PropertyValues)
    {
        super.updated(changedProperties);
        
        if (
            changedProperties.has("hideLabels") ||
            changedProperties.has("showDefaultIcons") ||
            changedProperties.has("evenlySpread") ||
            changedProperties.has("size")
        ) {    
            this._onSlotChange();
        }
    }
      
    private get _segmentedControlButtons() {
        const slot = this.shadowRoot?.querySelector("slot");
        const assignedNodes = slot?.assignedNodes({ flatten: true });
    
        const segmentedControlButtons: AtlSegmentedControlButton[] = [];
        assignedNodes?.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            if (element.tagName === TagNames.SegmentedControlButton.toUpperCase()) {
              segmentedControlButtons.push(element as AtlSegmentedControlButton);
            }
          }
        });
        return segmentedControlButtons;
    }

    private _onSlotChange = () => {
        const buttons = this._segmentedControlButtons;
        const buttonsLength = buttons ?.length
        
        buttons?.forEach((button, index) => {
            button._isFirstButton = index === 0;
            button._isLastButton = index === buttonsLength - 1;
            button.showDefaultIcon = this.showDefaultIcons && !button.iconName

            if (button.defaultActive && !this._defaultButtonActiveSet) {
                button.active = true;
                this._defaultButtonActiveSet = true;
            }

            button.requestUpdate();
        });
    
        this.requestUpdate();
    };

    render(){
        const classNames ={
            "segmented-control": true,
            "evenly-spread": this.evenlySpread === true
        };

        return html`
            <div class=${classMap(classNames)}  role="group">
                 <slot @slotchange=${this._onSlotChange}></slot>
            </div>
        `
    }
}